import { request, setCustomOnResponseError, resetCustomOnResponseError } from './axios'
import {
  TADConstant,
  TADCoveredEntities,
  TADCoveredEntity,
  TADGroups,
  TADInternalUserCreate,
  TADMembershipGroups,
  TADMemberships,
  TADUser,
  TADUserCreate,
  TADUsers,
  TADUserUpdate,
  TGroupDetail,
  TADPPRoles,
} from '../type'
import { createDownloadLink } from '../utility/download-link'
import { dateFormat, Format } from '../utility/date-format'
import { TADInternalUser, TADInternalUserUpdate, TADStreamsAndApps, TMeta, TMetaItemValue } from '../type/ad-management'
import { customErrorResponse } from '../utility/custom-error-response'

export const ADManagement = {
  constants: async (): Promise<TADConstant> => {
    setCustomOnResponseError(customErrorResponse)
    try {
      const { data } = await request({
        url: '/jarvis_api/meta/',
      })

      const values = data['ORG_UNITS_NAMES'] ?? []

      return values.reduce((acc: TADConstant, item: TMetaItemValue) => {
        acc[item.label as keyof TADConstant] = item.value
        return acc
      }, {})
    } finally {
      resetCustomOnResponseError()
    }
  },

  meta: async (): Promise<TMeta> => {
    setCustomOnResponseError(customErrorResponse)
    try {
      const { data } = await request({
        url: '/jarvis_api/meta/',
      })
      return data
    } finally {
      resetCustomOnResponseError()
    }
  },

  user: {
    create: async (data: TADUserCreate): Promise<void> => {
      await request({
        url: '/jarvis_api/users/',
        method: 'post',
        data,
      })
    },

    createInternal: async (data: TADInternalUserCreate): Promise<void> => {
      await request({
        url: '/jarvis_api/users/internal/',
        method: 'post',
        data,
      })
    },

    list: async (search = '', page_number = 1): Promise<TADUsers> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems = []
        if (search) {
          queryItems.push(`UserLookupExtended=${search}`)
        }
        if (!search) {
          queryItems.push(`page_number=${page_number}`)
        }

        const {
          data: { total_count, total_pages, page_no, max_page_size, results },
        } = await request({
          url: `/jarvis_api/users/?${queryItems.join('&')}`,
        })

        return {
          total_count,
          total_pages,
          page_no,
          max_page_size,
          results,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    internalList: async (search = '', page_number = 1, signal?: AbortSignal): Promise<TADUsers> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems = [`page_number=${page_number}`]
        if (search) {
          queryItems.push(`UserLookupExtended=${encodeURIComponent(search)}`)
        }

        const {
          data: { total_count, total_pages, page_no, max_page_size, results },
        } = await request({
          url: `/jarvis_api/users/in_folder/INT/?${queryItems.join('&')}`,
          signal,
        })

        return {
          total_count,
          total_pages,
          page_no,
          max_page_size,
          results,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    externalList: async (search = '', page_number = 1, signal?: AbortSignal): Promise<TADUsers> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems = [`page_number=${page_number}`]
        if (search) {
          queryItems.push(`UserLookupExtended=${encodeURIComponent(search)}`)
        }

        const {
          data: { total_count, total_pages, page_no, max_page_size, results },
        } = await request({
          url: `/jarvis_api/users/in_folder/EXT/?${queryItems.join('&')}`,
          signal,
        })

        return {
          total_count,
          total_pages,
          page_no,
          max_page_size,
          results,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    detail: async (user: string): Promise<TADUser | TADInternalUser> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          url: `/jarvis_api/users/detail/${user}/`,
        })

        return data
      } finally {
        resetCustomOnResponseError()
      }
    },

    enable: async (name: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/users/enable/${name}/`,
          method: 'patch',
        })
      } finally {
        resetCustomOnResponseError()
      }
    },

    disable: async (name: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/users/disable/${name}/`,
          method: 'patch',
        })
      } finally {
        resetCustomOnResponseError()
      }
    },

    updateInternal: async (user: TADInternalUserUpdate): Promise<TADInternalUser> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          url: '/jarvis_api/users/internal/',
          method: 'patch',
          data: user,
        })

        return data
      } finally {
        resetCustomOnResponseError()
      }
    },

    update: async (user: TADUserUpdate): Promise<TADUser> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          url: '/jarvis_api/users/',
          method: 'patch',
          data: user,
        })

        return data
      } finally {
        resetCustomOnResponseError()
      }
    },

    download: async (isExternal: boolean, search?: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          responseType: 'arraybuffer',
          url: `/jarvis_api/users/${isExternal ? '' : 'internal/'}export_to_excel/${search ? `?UserLookupExtended=${search}` : ''}`,
          method: 'post',
        })
        createDownloadLink(
          data,
          `AD_${isExternal ? 'External' : 'Internal'}_Users_${dateFormat(new Date(), Format['YYYY-MM-DD__HH:MM:SS'])}.xlsx`
        )
      } finally {
        resetCustomOnResponseError()
      }
    },
  },

  covered_entity: {
    list: async (search = '', page_number = 1, sharedGroups = false): Promise<TADCoveredEntities> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const shared = sharedGroups ? '&SharedGroups=including' : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/ce/?page_number=${page_number}${s}${shared}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    detail: async (name: string): Promise<TADCoveredEntity> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          url: `/jarvis_api/groups/memberships/member_of/${name}/?ReturnMembers=true`,
        })

        return data
      } finally {
        resetCustomOnResponseError()
      }
    },

    create: async (name: string, description: string, memberships: string[]): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: '/jarvis_api/groups/ce/',
          method: 'post',
          data: {
            Name: name,
            Description: description,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },

    update: async (name: string, description: string, newName: string, memberships: string[]): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: '/jarvis_api/groups/ce/',
          method: 'patch',
          data: {
            Name: name,
            Description: description,
            NewName: newName,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    download: async (search?: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const searchParam = search ? `GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const url = `/jarvis_api/groups/ce/export_to_excel/${searchParam ? `?${searchParam}` : ''}`

        const response = await request({
          responseType: 'arraybuffer',
          url,
          method: 'post',
        })
        const contentDisposition = response.headers['content-disposition']
        let fileName = `AD_CE__${dateFormat(new Date(), Format['YYYY-MM-DD__HH:MM:SS'])}.xlsx`

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1]
          }
        }

        createDownloadLink(response.data, fileName)
      } finally {
        resetCustomOnResponseError()
      }
    },
  },

  membership: {
    list: async (search = '', page_number = 1, type: '' | 'INT' | 'EXT' = '', signal?: AbortSignal): Promise<TADMemberships> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const t = type ? `&Type=${type}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/memberships/extended/ce_info/?page_number=${page_number}${s}${t}`,
          signal,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    listInternal: async (search = '', page_number = 1): Promise<TADMemberships> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/memberships/internal/?page_number=${page_number}${s}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    detail: async (name: string, isRecursive = false): Promise<TADMembershipGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          url: `/jarvis_api/groups/memberships/member_of/${name}/?ReturnMemberOf=true${isRecursive ? '&Recursive=true' : ''}`,
        })

        return data
      } finally {
        resetCustomOnResponseError()
      }
    },

    create: async (name: string, description: string, memberships: string[], type: 'INT' | 'EXT'): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: '/jarvis_api/groups/memberships/',
          method: 'post',
          data: {
            Name: name,
            Description: description,
            Memberships: memberships,
            Type: type,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },

    update: async (name: string, newName: string, description: string, memberships: string[], type: 'INT' | 'EXT'): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: '/jarvis_api/groups/memberships/',
          method: 'patch',
          data: {
            Name: name,
            NewName: newName,
            Description: description,
            Memberships: memberships,
            Type: type,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    download: async (isExternal: boolean, search?: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const typeParam = `Type=${isExternal ? 'EXT' : 'INT'}`
        const searchParam = search ? `GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const query = [typeParam, searchParam].filter(Boolean).join('&')
        const url = `/jarvis_api/groups/memberships/export_to_excel/${query ? `?${query}` : ''}`

        const response = await request({
          responseType: 'arraybuffer',
          url,
          method: 'post',
        })
        const contentDisposition = response.headers['content-disposition']
        let fileName = `AD_${typeParam}_Membership_${dateFormat(new Date(), Format['YYYY-MM-DD__HH:MM:SS'])}.xlsx`

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1]
          }
        }

        createDownloadLink(response.data, fileName)
      } finally {
        resetCustomOnResponseError()
      }
    },
  },

  group: {
    covered_entity: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/ce/?SharedGroups=only&page_number=${page_number}${s}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    ce: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/ce/?page_number=${page_number}${s}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    ce_groups: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/ce/?SharedGroups=only&page_number=${page_number}${s}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    pharmacies: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/pharmacies/?page_number=${page_number}${s}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    pharmacy_groups: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/pharmacies/?SharedGroups=only&page_number=${page_number}${s}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    patient_sources: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/patient_sources/?&page_number=${page_number}${s}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    patient_source_groups: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/patient_sources/?SharedGroups=only&page_number=${page_number}${s}`,
        })
        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    distributor: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/distributors/?page_number=${page_number}${s}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    distributor_groups: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/distributors/?SharedGroups=only&page_number=${page_number}${s}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    qlik_app: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/qlik/apps/?page_number=${page_number}${s}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    qlik_stream: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/qlik/streams/?page_number=${page_number}${s}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    qlik_groups: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/qlik/shared_groups/?SharedGroups=only&page_number=${page_number}${s}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    pharmacy: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/pharmacies/?page_number=${page_number}${s}&SharedGroups=including`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    omit: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/omissions/?page_number=${page_number}${s}&SharedGroups=including`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    omit_groups: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/omissions/?page_number=${page_number}${s}&SharedGroups=only`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    distributors: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/distributors/?page_number=${page_number}${s}&SharedGroups=including`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    manufacturers: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/manufacturers/?page_number=${page_number}${s}&SharedGroups=including`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    measures: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/omissions/?page_number=${page_number}${s}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    other: async (search = '', page_number = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/others/?page_number=${page_number}${s}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    all_details: async (search = ''): Promise<TGroupDetail> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          url: `/jarvis_api/groups/details/all/${search}/`,
        })

        return data
      } finally {
        resetCustomOnResponseError()
      }
    },
  },
  streamsAndApplications: {
    list: async (search = '', page_number = 1, streamsOnly: boolean, signal?: AbortSignal): Promise<TADStreamsAndApps> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems = [`page_number=${page_number}`]
        if (search) {
          queryItems.push(`GroupLookupExtended=${encodeURIComponent(search)}`)
        }

        const {
          data: { total_count, total_pages, page_no, max_page_size, results },
        } = await request({
          url: `/jarvis_api/groups/qlik/${streamsOnly ? 'streams' : 'apps'}/?${queryItems.join('&')}`,
          signal,
        })

        return {
          total_count,
          total_pages,
          page_no,
          max_page_size,
          results,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    update: async (name: string, description: string, newName: string, memberships: string[], isStream: boolean): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/qlik/${isStream ? 'streams' : 'apps'}/`,
          method: 'patch',
          data: {
            Name: name,
            Description: description,
            NewName: newName,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },

    create: async (name: string, description: string, memberships: string[], isStream: boolean): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/qlik/${isStream ? 'streams' : 'apps'}/`,
          method: 'post',
          data: {
            Name: name,
            Description: description,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    download: async (isStream: boolean, search?: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const searchParam = search ? `GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const url = `/jarvis_api/groups/qlik/${isStream ? 'streams' : 'apps'}/export_to_excel/${searchParam ? `?${searchParam}` : ''}`

        const response = await request({
          responseType: 'arraybuffer',
          url,
          method: 'post',
        })
        const contentDisposition = response.headers['content-disposition']
        let fileName = `AD_CE__${dateFormat(new Date(), Format['YYYY-MM-DD__HH:MM:SS'])}.xlsx`

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1]
          }
        }

        createDownloadLink(response.data, fileName)
      } finally {
        resetCustomOnResponseError()
      }
    },
  },
  sharedGroups: {
    list: async (
      search = '',
      page_number = 1,
      sufix: string,
      sharedGroupsParam: string | null,
      signal?: AbortSignal
    ): Promise<TADMemberships> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems = [`page_number=${page_number}`]
        if (search) {
          queryItems.push(`GroupLookupExtended=${encodeURIComponent(search)}`)
        }
        if (sharedGroupsParam) {
          queryItems.push(`SharedGroups=${sharedGroupsParam}`)
        }
        const {
          data: { total_count, total_pages, page_no, max_page_size, results },
        } = await request({
          url: `/jarvis_api/groups/${sufix}?${queryItems.join('&')}`,
          signal,
        })

        return {
          total_count,
          total_pages,
          page_no,
          max_page_size,
          results,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    update: async (name: string, description: string, newName: string, memberships: string[], sufix: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/${sufix}`,
          method: 'patch',
          data: {
            Name: name,
            Description: description,
            NewName: newName,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },

    create: async (name: string, description: string, memberships: string[], sufix: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/${sufix}`,
          method: 'post',
          data: {
            Name: name,
            Description: description,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    download: async (queryUrl: string, search?: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const searchParam = search ? `GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const sharedGroupsParam = 'SharedGroups=only'
        const query = [searchParam, sharedGroupsParam].filter(Boolean).join('&')
        const url = `${queryUrl}${query ? `?${query}` : ''}`

        const response = await request({
          responseType: 'arraybuffer',
          url,
          method: 'post',
        })
        const contentDisposition = response.headers['content-disposition']
        let fileName = `AD_SharedGroups__${dateFormat(new Date(), Format['YYYY-MM-DD__HH:MM:SS'])}.xlsx`

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/)
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1]
          }
        }

        createDownloadLink(response.data, fileName)
      } finally {
        resetCustomOnResponseError()
      }
    },
  },
  entities: {
    list: async (
      search = '',
      page_number = 1,
      sufix: string,
      entitiesParam: string | null,
      signal?: AbortSignal
    ): Promise<TADMemberships> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems = [`page_number=${page_number}`]
        if (search) {
          queryItems.push(`GroupLookupExtended=${encodeURIComponent(search)}`)
        }
        if (entitiesParam) {
          queryItems.push(`SharedGroups=${entitiesParam}`)
        }
        const {
          data: { total_count, total_pages, page_no, max_page_size, results },
        } = await request({
          url: `/jarvis_api/groups/${sufix}?${queryItems.join('&')}`,
          signal,
        })

        return {
          total_count,
          total_pages,
          page_no,
          max_page_size,
          results,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    update: async (id: string, name: string, newName: string, memberships: string[], type: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/${type}/`,
          method: 'patch',
          data: {
            Name: id,
            Description: name,
            NewName: newName,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },

    create: async (name: string, description: string, memberships: string[], type: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/${type}/`,
          method: 'post',
          data: {
            Name: name,
            Description: description,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    download: async (queryUrl: string, search?: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const searchParam = search ? `GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const url = `${queryUrl}${searchParam ? `?${searchParam}` : ''}`

        const response = await request({
          responseType: 'arraybuffer',
          url,
          method: 'post',
        })
        const contentDisposition = response.headers['content-disposition']
        let fileName = `AD_Entities_${dateFormat(new Date(), Format['YYYY-MM-DD__HH:MM:SS'])}.xlsx`

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1]
          }
        }

        createDownloadLink(response.data, fileName)
      } finally {
        resetCustomOnResponseError()
      }
    },
  },
  pp_roles: {
    list: async (search = '', page_number = 1, sharedGroups = false): Promise<TADPPRoles> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const shared = sharedGroups ? '&SharedGroups=including' : ''

        const {
          data: { results, max_page_size, page_no, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/pp_roles/?page_number=${page_number}${s}${shared}`,
        })

        return {
          results,
          max_page_size,
          page_no,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    create: async (name: string, description: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: '/jarvis_api/groups/pp_roles/',
          method: 'post',
          data: {
            Name: name,
            Description: description,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    update: async (name: string, description: string, newName: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: '/jarvis_api/groups/pp_roles/',
          method: 'patch',
          data: {
            Name: name,
            Description: description,
            NewName: newName,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
  },
}
